// Master Apps · seletor de múltipla escolha reutilizável.
//
// Campo com chips removíveis + painel com busca, checkbox por item, ações em
// lote e estados de carregando/vazio/erro. Nasceu para a etapa "Acesso e
// confidencialidade" do cadastro (unidades, setores e colaboradores do TOTVS),
// mas não conhece nada desse domínio: recebe `MultiSelectOption[]`.
//
// O painel vai para o `document.body` via portal com `position: fixed` e a
// posição é calculada do `getBoundingClientRect()` do campo (abre para baixo,
// sobe quando não couber, clamp na viewport, reposiciona em scroll/resize) —
// mesma técnica do `InfoPopover` e do `StackPicker`, para nunca ser cortado
// pelo container de scroll do formulário.
//
// Responsivo por `useViewport()`: no compacto (≤1024px) o painel para de seguir
// a largura do campo e ocupa a tela quase toda, as linhas passam de 33px para
// 43px de alvo de toque e a busca usa 16px de fonte (abaixo disso o Safari do
// iPhone dá zoom ao focar). O desktop é idêntico ao de antes.

import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, IconChevronD, IconSearch, IconX } from '@/components/icons';
import { byViewport, useViewport, type Viewport } from '@/hooks/use-viewport';
import { fontMono, fontSans, makePalette, NOC_RED, numeroTabular } from '@/lib/theme';

const DANGER = '#dc2626';
const EDGE = 8;
const GAP = 4;
/**
 * Largura mínima do painel no desktop. 380 e não 260: o rótulo real de uma
 * filial ("2 · MASTERBOI LTDA - FILIAL BR") não cabia em 260px e virava
 * reticências — a lista precisa mostrar o nome COMPLETO do que se escolhe.
 */
const PANEL_MIN_W = 380;
/** Altura desejada do painel; o espaço real na tela pode reduzi-la. */
const DESIRED_H = 380;
/** Altura mínima negociável: abaixo disso o painel sai de baixo do campo. */
const MIN_H = 180;

// ─── Tipos ────────────────────────────────────
export interface MultiSelectOption {
  value: string;
  label: string;
  /** Detalhe discreto à direita do label (ex.: "412 pessoas"). */
  hint?: string;
  /** Badges pequenos abaixo do label (ex.: unidades que possuem o setor). */
  meta?: string[];
  /** Ponto colorido antes do label. */
  color?: string;
}

export interface MultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: MultiSelectOption[];
  dark: boolean;
  accent?: string;
  /** Texto do campo quando nada está escolhido. */
  placeholder?: string;
  /** `[singular, plural]` usado no contador ("1 unidade" / "3 unidades"). */
  noun?: [string, string];
  searchPlaceholder?: string;
  /** Rótulo acessível do campo e da lista. */
  ariaLabel?: string;
  /** Opções ainda carregando. */
  loading?: boolean;
  /** Mensagem de erro do carregamento das opções. */
  error?: string | null;
  /** Texto quando a lista chega vazia. */
  emptyLabel?: string;
  /**
   * Campo de ESCOLHA ÚNICA reusando este painel (o `Select` da casa não tem
   * busca, e com 200+ setores buscar é obrigatório). O efeito é esconder
   * "Selecionar todos": quem só pode ficar com um valor não tem o que fazer com
   * um botão que marca a lista inteira — o chamador descartaria tudo menos o
   * último, e o usuário veria 204 itens acenderem e apagarem sem entender.
   * "Limpar" continua, porque desfazer a escolha é operação legítima.
   */
  escolhaUnica?: boolean;
  /** Desliga o controle (ex.: setores antes de escolher a unidade). */
  disabled?: boolean;
  /** Explicação do estado desabilitado · aparece no lugar do placeholder. */
  disabledLabel?: string;
  /** Teto de linhas renderizadas · acima disso o painel pede refino da busca. */
  maxVisible?: number;
  /** Máximo de chips no campo antes de resumir o restante em "+N". */
  maxChips?: number;
  /**
   * Quando informado, a busca é do SERVIDOR: o filtro local é desligado (as
   * opções já chegam filtradas) e cada tecla é repassada ao chamador, que
   * aplica debounce no hook de dados.
   */
  onSearchChange?: (query: string) => void;
  invalid?: boolean;
  /** Injetados pelo FormField. */
  id?: string;
  'aria-required'?: true;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
}

interface PanelPos {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

// ─── Utilidades ───────────────────────────────
/** Minúsculas sem acento · "Pecuária" casa com "pecuaria". */
function norm(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function samePos(a: PanelPos | null, b: PanelPos): boolean {
  return (
    a !== null &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.maxHeight === b.maxHeight
  );
}

/**
 * Geometria do painel a partir do retângulo do campo. Pura e fora do
 * componente: é a única regra de "onde o painel nasce".
 *
 * No compacto ele deixa de seguir a largura do campo e passa a ocupar a tela
 * quase toda (100vw − 2×8px). O motivo é medido: num formulário de 320px o campo
 * de setores tinha 79px de largura, e um painel de 260px (o piso do desktop)
 * nascia colado numa borda com a lista ilegível.
 */
function computePanelPos(r: DOMRect, vw: number, vh: number, compact: boolean): PanelPos {
  const width = compact ? vw - 2 * EDGE : Math.min(Math.max(r.width, PANEL_MIN_W), vw - 2 * EDGE);
  const left = compact ? EDGE : Math.min(Math.max(EDGE, r.left), Math.max(EDGE, vw - width - EDGE));

  // No celular a tela é mais alta que larga: 380px de teto mostravam 4 linhas de
  // 44px, então o painel pode tomar até 70% da altura (com rolagem por dentro).
  const desired = compact ? Math.min(Math.round(vh * 0.7), 460) : DESIRED_H;
  const below = Math.floor(vh - r.bottom - EDGE - GAP);
  // SEMPRE para BAIXO (pedido de produto: painel abrindo para cima desorienta).
  // Quando o espaço abaixo do campo não basta, o clamp do `fits` desliza o
  // painel para cima até caber INTEIRO na tela — ele pode cobrir o campo, mas
  // nunca nasce invertido nem estoura a borda.
  const maxHeight = Math.min(desired, Math.max(MIN_H, below));

  // Último ponto em que um painel desta altura ainda cabe inteiro na tela. É o
  // conserto do painel que "nascia fora da tela": com o campo no fim da página o
  // espaço dos dois lados era menor que os 180px do piso, e o painel ancorado em
  // `top: r.bottom + 4` estourava a borda de baixo sem jeito de rolar até ele.
  // Aqui ele encosta na margem e cobre um pedaço do campo — o que é justamente o
  // comportamento de um seletor de celular.
  const fits = Math.max(EDGE, vh - maxHeight - EDGE);
  return { top: Math.min(Math.max(EDGE, r.bottom + GAP), fits), left, width, maxHeight };
}

function frameStyle(dark: boolean, accent: string, focused: boolean, invalid?: boolean) {
  const p = makePalette(dark);
  // A COR da borda prioriza o erro (o campo inválido continua vermelho mesmo
  // com foco); o HALO prioriza o foco (indica onde o teclado está).
  let color = p.border;
  if (invalid) color = DANGER;
  else if (focused) color = accent;

  let boxShadow = 'none';
  if (focused) boxShadow = `0 0 0 3px ${p.ring}`;
  else if (invalid) boxShadow = `0 0 0 3px ${DANGER}22`;

  return {
    border: `1px solid ${color}`,
    boxShadow,
    transition: 'border-color 120ms, box-shadow 120ms',
  };
}

/**
 * Moldura do campo: os três estados visuais (desabilitado, foco/aberto e
 * inválido) num só lugar, para o JSX não repetir `disabled ? … : …` por
 * declaração.
 */
function fieldStyle(
  dark: boolean,
  accent: string,
  state: Readonly<{ disabled: boolean; focused: boolean; open: boolean; invalid?: boolean }>,
  vp: Viewport,
): CSSProperties {
  const p = makePalette(dark);
  return {
    position: 'relative',
    // 44px no toque: o campo inteiro é o alvo (o botão invisível cobre `inset:
    // 0`), então esta altura é literalmente o tamanho do alvo de abrir.
    minHeight: byViewport(vp, { mobile: 44, desktop: 38 }),
    padding: byViewport(vp, { mobile: '7px 34px 7px 9px', desktop: '5px 32px 5px 8px' }),
    background: state.disabled ? p.surface2 : p.inputBg,
    borderRadius: 7,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    alignItems: 'center',
    cursor: state.disabled ? 'not-allowed' : 'pointer',
    opacity: state.disabled ? 0.7 : 1,
    ...frameStyle(dark, accent, (state.focused || state.open) && !state.disabled, state.invalid),
  };
}

/** Botão invisível que cobre o campo inteiro (abre com clique em qualquer ponto). */
function openerStyle(disabled: boolean): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    padding: 0,
    margin: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: 7,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

/** Texto do campo sem nada escolhido: desabilitado explica o motivo, quando houver. */
function fieldPlaceholder(
  disabled: boolean,
  disabledLabel: string | undefined,
  placeholder: string,
): string {
  return disabled ? (disabledLabel ?? placeholder) : placeholder;
}

/**
 * Fundo e borda do chip derivam do acento, mais fortes no escuro. Calculados
 * aqui (e não no style) para não aninhar template dentro de template na
 * interpolação da borda.
 */
function chipColors(dark: boolean, accent: string): { bg: string; border: string } {
  return dark
    ? { bg: `${accent}22`, border: `${accent}44` }
    : { bg: `${accent}12`, border: `${accent}33` };
}

/** Contador do campo: "1 unidade" / "3 unidades", conforme o par `noun`. */
function countLabelOf(count: number, noun: readonly [string, string]): string {
  return `${count} ${count === 1 ? noun[0] : noun[1]}`;
}

/** Campo de busca do painel: fundo de input + a mesma moldura de foco do campo. */
function searchFieldStyle(
  dark: boolean,
  accent: string,
  focused: boolean,
  vp: Viewport,
): CSSProperties {
  const p = makePalette(dark);
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    // 38 no desktop (era 32): a busca é a porta de entrada do painel — campo
    // maior = mais fácil de achar e de acertar com o mouse.
    height: byViewport(vp, { mobile: 44, desktop: 38 }),
    padding: byViewport(vp, { mobile: '0 10px', desktop: '0 10px' }),
    background: p.inputBg,
    borderRadius: 6,
    ...frameStyle(dark, accent, focused),
  };
}

/**
 * Moldura do painel no portal: `position: fixed` na posição medida do campo.
 * Enquanto `pos` é null (o paint anterior à medição) o painel fica invisível e
 * sem eventos, para não piscar no canto da tela antes de ir para o lugar.
 */
function panelStyle(pos: PanelPos | null, dark: boolean): CSSProperties {
  const p = makePalette(dark);
  return {
    position: 'fixed',
    top: pos?.top,
    bottom: pos?.bottom,
    left: pos?.left ?? 0,
    width: pos?.width ?? 300,
    maxHeight: pos?.maxHeight ?? 380,
    visibility: pos ? 'visible' : 'hidden',
    pointerEvents: pos ? 'auto' : 'none',
    zIndex: 8001,
    display: 'flex',
    flexDirection: 'column',
    background: p.surface,
    border: `1px solid ${p.border}`,
    borderRadius: 8,
    boxShadow: '0 14px 40px -12px rgba(0,0,0,0.28)',
    overflow: 'hidden',
    fontFamily: fontSans,
  };
}

/**
 * Índice ativo depois de uma seta, circulando nas pontas (a última desce para a
 * primeira e vice-versa). Lista vazia fica sempre em 0.
 */
function stepActive(current: number, count: number, delta: 1 | -1): number {
  if (count === 0) return 0;
  if (delta === 1) return current + 1 >= count ? 0 : current + 1;
  return current <= 0 ? count - 1 : current - 1;
}

// ─── Componente ───────────────────────────────
export function MultiSelect({
  value,
  onChange,
  options,
  dark,
  accent = NOC_RED,
  placeholder = 'Selecione…',
  noun = ['item', 'itens'],
  searchPlaceholder = 'Buscar…',
  ariaLabel,
  loading = false,
  error = null,
  emptyLabel = 'Nenhuma opção disponível',
  escolhaUnica = false,
  disabled = false,
  disabledLabel,
  maxVisible = 200,
  maxChips = 8,
  onSearchChange,
  invalid,
  ...aria
}: Readonly<MultiSelectProps>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [pos, setPos] = useState<PanelPos | null>(null);

  const fieldRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);

  const serverSearch = typeof onSearchChange === 'function';

  // `role="combobox"` exige `aria-expanded` + `aria-controls`: o botão precisa
  // apontar para o popup que ele abre (a lista, no portal).
  const listboxId = useId();

  const optionByValue = useMemo(
    () => new Map(options.map((o) => [o.value, o] as const)),
    [options],
  );

  // Rótulo dos escolhidos que saíram da lista atual: a busca no servidor troca
  // as opções, mas o chip precisa continuar legível.
  const labelCache = useRef(new Map<string, MultiSelectOption>());
  useEffect(() => {
    for (const o of options) labelCache.current.set(o.value, o);
  }, [options]);
  const optionOf = (v: string) => optionByValue.get(v) ?? labelCache.current.get(v);

  const selected = useMemo(() => new Set(value), [value]);

  // ─── Posicionamento do painel ──
  const measure = useCallback(() => {
    const el = fieldRef.current;
    if (!el) return;
    const next = computePanelPos(
      el.getBoundingClientRect(),
      window.innerWidth,
      window.innerHeight,
      vp.compact,
    );
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, [vp.compact]);

  const dismiss = useCallback(
    (fromKeyboard: boolean) => {
      setOpen(false);
      setQuery('');
      setActive(0);
      setPos(null);
      onSearchChange?.('');
      if (fromKeyboard) btnRef.current?.focus();
    },
    [onSearchChange],
  );

  useLayoutEffect(() => {
    if (!open) return;
    // Rola o campo para o MEIO do scroll antes de medir: com espaço garantido
    // abaixo, o painel abre para baixo de verdade (pedido de produto) — sem
    // isso, campo no pé da tela fazia o clamp empurrar o painel por cima dele.
    fieldRef.current?.scrollIntoView({ block: 'center' });
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss(true);
    };
    // `measure` ignora o argumento do evento, então assina direto — sem wrapper.
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, measure, dismiss]);

  useEffect(() => {
    if (!open) return;
    // rAF: o input vive num portal que monta neste mesmo commit — focar
    // direto no efeito às vezes rodava antes de ele existir na árvore.
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (active >= 0) activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // ─── Lista ──
  // Na busca do servidor as opções já vêm filtradas — filtrar de novo aqui
  // esconderia acertos por matrícula (o label é o nome).
  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (serverSearch || q === '') return options;
    return options.filter((o) => {
      if (norm(o.label).includes(q) || norm(o.value).includes(q)) return true;
      return (o.meta ?? []).some((m) => norm(m).includes(q));
    });
  }, [options, query, serverSearch]);

  const visible = useMemo(() => filtered.slice(0, maxVisible), [filtered, maxVisible]);
  const truncated = filtered.length - visible.length;
  // Busca no servidor: a resposta já vem cortada pelo `limit`, então uma lista
  // cheia significa "pode ter mais" mesmo sem truncagem local.
  const capped = serverSearch && filtered.length >= maxVisible;

  const toggle = useCallback(
    (optValue: string) => {
      onChange(selected.has(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue]);
    },
    [onChange, selected, value],
  );

  const selectAllVisible = () => {
    const next = [...value];
    for (const o of visible) if (!selected.has(o.value)) next.push(o.value);
    onChange(next);
  };

  // Digitar e limpar a busca fazem a MESMA coisa: trocam o texto, voltam o
  // destaque para a primeira linha e avisam o chamador (busca no servidor).
  const onQuery = (next: string) => {
    setQuery(next);
    setActive(0);
    onSearchChange?.(next);
  };

  const onSearchKey = (e: React.KeyboardEvent) => {
    const count = visible.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => stepActive(i, count, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => stepActive(i, count, -1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(Math.max(0, count - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = visible[active];
      if (opt) toggle(opt.value);
    } else if (e.key === 'Tab') {
      dismiss(false);
    }
  };

  const onFieldKey = (e: React.KeyboardEvent) => {
    if (open) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActive(0);
      setOpen(true);
    }
  };

  const count = value.length;
  const countLabel = countLabelOf(count, noun);
  const chips = value.slice(0, maxChips);
  const hiddenChips = count - chips.length;

  // Texto (e cor) do painel sem linhas, na ordem em que a informação importa:
  // falha do carregamento, carregamento em curso, busca local sem acerto e, por
  // último, a lista que chegou realmente vazia.
  let emptyText = emptyLabel;
  let emptyColor = p.muted;
  if (error) {
    emptyText = error;
    emptyColor = DANGER;
  } else if (loading) emptyText = 'Carregando…';
  else if (query.trim() !== '' && !serverSearch) emptyText = 'Nada encontrado para esta busca';

  const chip = chipColors(dark, accent);

  // Rodapé: avisa quando a lista está cortada — localmente (`truncated`) ou já
  // pelo `limit` do servidor (`capped`) — e senão ensina os atalhos. O aviso
  // ganha a cor mais forte; o atalho fica no cinza discreto.
  // No compacto o atalho de teclado não existe (e ocupava duas linhas em 320px):
  // o rodapé passa a explicar o gesto que serve ali.
  let footerText = byViewport(vp, {
    mobile: 'Toque para marcar · toque fora para fechar',
    desktop: 'Setas para navegar · Enter para marcar · Esc para fechar',
  });
  let footerColor = p.muted;
  if (truncated > 0) {
    footerText = `Mostrando ${visible.length.toLocaleString('pt-BR')} de ${filtered.length.toLocaleString('pt-BR')}. Refine a busca para ver o restante.`;
    footerColor = p.textMuted;
  } else if (capped) {
    footerText = `Mostrando os primeiros ${visible.length.toLocaleString('pt-BR')}. Refine a busca para encontrar alguém específico.`;
    footerColor = p.textMuted;
  }

  return (
    <>
      {/* Campo · chips + contador. O botão invisível cobre a área toda para
          abrir com um clique em qualquer ponto, sem aninhar botões (os "x" dos
          chips ficam por cima com pointerEvents próprio). */}
      <div
        ref={fieldRef}
        style={fieldStyle(dark, accent, { disabled, focused, open, invalid }, vp)}
      >
        <button
          {...aria}
          ref={btnRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => {
            setActive(0);
            setOpen((v) => !v);
          }}
          onKeyDown={onFieldKey}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={openerStyle(disabled)}
        />

        {count === 0 ? (
          <span
            style={{
              position: 'relative',
              pointerEvents: 'none',
              fontSize: byViewport(vp, { mobile: 13.5, desktop: 12.5 }),
              color: p.placeholder,
              fontFamily: fontSans,
              padding: '2px 2px',
              lineHeight: 1.5,
            }}
          >
            {fieldPlaceholder(disabled, disabledLabel, placeholder)}
          </span>
        ) : (
          <>
            {chips.map((v) => {
              const opt = optionOf(v);
              return (
                <span
                  key={v}
                  style={{
                    position: 'relative',
                    pointerEvents: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    maxWidth: '100%',
                    padding: byViewport(vp, {
                      mobile: '3px 3px 3px 8px',
                      desktop: '3px 3px 3px 7px',
                    }),
                    borderRadius: 5,
                    fontSize: byViewport(vp, { mobile: 12.5, desktop: 11.5 }),
                    fontWeight: 500,
                    background: chip.bg,
                    color: p.text,
                    border: `1px solid ${chip.border}`,
                    fontFamily: fontSans,
                  }}
                >
                  {opt?.color && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 2,
                        background: opt.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {opt?.label ?? v}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remover ${opt?.label ?? v}`}
                    disabled={disabled}
                    onClick={() => onChange(value.filter((x) => x !== v))}
                    style={{
                      pointerEvents: 'auto',
                      // 26px no toque. Não vai a 40 porque o "x" vive DENTRO do
                      // chip: um alvo de 40px faria cada chip ter 46px de altura
                      // e três escolhas ocupariam meia tela. 26px é o maior alvo
                      // que não deforma o chip.
                      width: byViewport(vp, { mobile: 26, desktop: 17 }),
                      height: byViewport(vp, { mobile: 26, desktop: 17 }),
                      borderRadius: 3,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: p.muted,
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    <IconX size={byViewport(vp, { mobile: 12, desktop: 10 })} />
                  </button>
                </span>
              );
            })}
            {hiddenChips > 0 && (
              <span
                style={{
                  ...numeroTabular,
                  position: 'relative',
                  pointerEvents: 'none',
                  padding: '3px 7px',
                  borderRadius: 5,
                  fontSize: 11,
                  fontWeight: 700,
                  background: p.surface2,
                  border: `1px solid ${p.border}`,
                  color: p.muted,
                }}
              >
                +{hiddenChips}
              </span>
            )}
            <span
              style={{
                position: 'relative',
                pointerEvents: 'none',
                marginLeft: 'auto',
                paddingLeft: 6,
                fontSize: byViewport(vp, { mobile: 11.5, desktop: 10.5 }),
                fontWeight: 700,
                color: p.muted,
                fontFamily: fontSans,
                whiteSpace: 'nowrap',
              }}
            >
              {countLabel}
            </span>
          </>
        )}

        <IconChevronD
          size={14}
          style={{
            position: 'absolute',
            right: 10,
            top: 'calc(50% - 7px)',
            color: p.muted,
            pointerEvents: 'none',
          }}
        />
      </div>

      {open &&
        createPortal(
          <>
            {/* fundo invisível: fecha ao clicar fora sem roubar o foco */}
            <div
              aria-hidden="true"
              onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
              onClick={() => dismiss(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 8000 }}
            />
            <div data-mapp-panel="" style={panelStyle(pos, dark)}>
              {/* Busca */}
              <div style={{ padding: 8, borderBottom: `1px solid ${p.border}`, flexShrink: 0 }}>
                <div style={searchFieldStyle(dark, accent, searchFocused, vp)}>
                  <IconSearch size={13} style={{ color: p.muted, flexShrink: 0 }} />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    onKeyDown={onSearchKey}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      // `stretch` no compacto: sem isso a caixa do input tem a
                      // altura do texto (24px medidos) dentro de um campo de
                      // 44px, e o toque na faixa de cima ou de baixo não focava
                      // nada. Esticado, o alvo é o campo inteiro.
                      alignSelf: byViewport(vp, { mobile: 'stretch', desktop: undefined }),
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: p.text,
                      // 16px no compacto NÃO é estética: com fonte menor que 16
                      // o Safari do iPhone dá zoom automático ao focar o input, e
                      // a página fica deslocada depois de fechar o painel.
                      fontSize: byViewport(vp, { mobile: 16, desktop: 12.5 }),
                      fontFamily: fontSans,
                    }}
                  />
                  {query !== '' && (
                    <button
                      type="button"
                      aria-label="Limpar busca"
                      onClick={() => {
                        onQuery('');
                        searchRef.current?.focus();
                      }}
                      style={{
                        // Cabe inteiro na altura do campo de busca compacto
                        // (44px): 36px de alvo, contra 18px antes.
                        width: byViewport(vp, { mobile: 36, desktop: 18 }),
                        height: byViewport(vp, { mobile: 36, desktop: 18 }),
                        borderRadius: 3,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        color: p.muted,
                        cursor: 'pointer',
                        padding: 0,
                        flexShrink: 0,
                      }}
                    >
                      <IconX size={byViewport(vp, { mobile: 12, desktop: 10 })} />
                    </button>
                  )}
                </div>
              </div>

              {/* Ações em lote */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: byViewport(vp, { mobile: '7px 10px', desktop: '6px 10px' }),
                  borderBottom: `1px solid ${p.border}`,
                  background: p.surface2,
                  flexShrink: 0,
                }}
              >
                {!escolhaUnica && (
                  <BulkAction
                    dark={dark}
                    accent={accent}
                    disabled={visible.length === 0}
                    onClick={selectAllVisible}
                  >
                    Selecionar todos
                  </BulkAction>
                )}
                <BulkAction
                  dark={dark}
                  accent={accent}
                  disabled={count === 0}
                  onClick={() => onChange([])}
                >
                  Limpar
                </BulkAction>
                <span
                  aria-live="polite"
                  style={{
                    // Contador "N/M": a sans com figuras tabulares serve os dois
                    // estados, então o ternário de fonte de antes saiu.
                    ...numeroTabular,
                    marginLeft: 'auto',
                    fontSize: byViewport(vp, { mobile: 11.5, desktop: 10.5 }),
                    color: p.muted,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loading ? 'buscando…' : `${count}/${filtered.length}`}
                </span>
              </div>

              {/* Lista */}
              {visible.length === 0 ? (
                <div
                  style={{
                    padding: '22px 16px',
                    textAlign: 'center',
                    fontSize: byViewport(vp, { mobile: 13, desktop: 12 }),
                    lineHeight: 1.55,
                    color: emptyColor,
                    fontFamily: fontSans,
                  }}
                >
                  {emptyText}
                </div>
              ) : (
                <div
                  id={listboxId}
                  role="listbox"
                  aria-label={ariaLabel ?? 'Opções'}
                  aria-multiselectable="true"
                  style={{ overflowY: 'auto', minHeight: 0, padding: 4 }}
                >
                  {visible.map((o, i) => {
                    const isSelected = selected.has(o.value);
                    const isActive = i === active;
                    return (
                      <div
                        key={o.value}
                        ref={isActive ? activeRef : undefined}
                        role="option"
                        aria-selected={isSelected}
                        tabIndex={-1}
                        onClick={() => toggle(o.value)}
                        // A linha continua <div role="option"> porque é filha de
                        // um role="listbox" (um <button> aqui quebraria a árvore
                        // ARIA do combobox). O teclado principal é o do campo de
                        // busca (setas + Enter); este handler cobre a linha que
                        // ganhou foco pelo clique, para Enter/Espaço marcarem em
                        // vez de não fazerem nada.
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(o.value);
                          }
                        }}
                        onMouseEnter={() => setActive(i)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          // O alvo de toque da linha É o padding: com 7px em cima
                          // e embaixo a linha media 33px (medido em 320px), abaixo
                          // do mínimo de 40. Com 12px ela passa de 43px.
                          padding: byViewport(vp, { mobile: '12px 10px', desktop: '7px 9px' }),
                          borderRadius: 5,
                          cursor: 'pointer',
                          background: isActive ? p.surface2 : 'transparent',
                        }}
                      >
                        <span
                          style={{
                            width: byViewport(vp, { mobile: 18, desktop: 16 }),
                            height: byViewport(vp, { mobile: 18, desktop: 16 }),
                            marginTop: 1,
                            borderRadius: 4,
                            background: isSelected ? accent : 'transparent',
                            border: `1.5px solid ${isSelected ? accent : p.borderStrong}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: '#fff',
                          }}
                        >
                          {isSelected && (
                            <IconCheck size={byViewport(vp, { mobile: 12, desktop: 11 })} />
                          )}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {o.color && (
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 2,
                                  background: o.color,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: byViewport(vp, { mobile: 14, desktop: 12.5 }),
                                fontWeight: isSelected ? 700 : 500,
                                color: p.text,
                                // Nome COMPLETO em até 2 linhas (era reticência
                                // numa linha): escolher filial exige ler a razão
                                // social inteira — truncar escondia exatamente o
                                // que diferencia uma da outra.
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflowWrap: 'anywhere',
                                lineHeight: 1.35,
                              }}
                            >
                              {o.label}
                            </span>
                            {o.hint && (
                              <span
                                style={{
                                  fontSize: byViewport(vp, { mobile: 11.5, desktop: 10.5 }),
                                  color: p.muted,
                                  // Mono porque o hint carrega matrícula (ver
                                  // register-screen): é identificador.
                                  fontFamily: fontMono,
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {o.hint}
                              </span>
                            )}
                          </span>
                          {o.meta && o.meta.length > 0 && (
                            <span
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 4,
                                marginTop: 5,
                              }}
                            >
                              {o.meta.map((m) => (
                                <span
                                  key={m}
                                  title={m}
                                  style={{
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    color: p.muted,
                                    background: dark ? p.surface3 : p.surface2,
                                    border: `1px solid ${p.border}`,
                                    fontFamily: fontSans,
                                  }}
                                >
                                  {m}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Rodapé · truncagem e atalhos */}
              <div
                style={{
                  padding: '7px 10px',
                  borderTop: `1px solid ${p.border}`,
                  background: p.surface2,
                  fontSize: byViewport(vp, { mobile: 11.5, desktop: 10.5 }),
                  color: footerColor,
                  fontFamily: fontSans,
                  lineHeight: 1.45,
                  flexShrink: 0,
                }}
              >
                {footerText}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// ─── Ação em lote do painel ───────────────────
function BulkAction({
  children,
  dark,
  accent,
  disabled,
  onClick,
}: Readonly<{
  children: ReactNode;
  dark: boolean;
  accent: string;
  disabled?: boolean;
  onClick: () => void;
}>) {
  const p = makePalette(dark);
  // Lê a faixa por conta própria em vez de receber por prop: é o mesmo custo e
  // evita mais um parâmetro atravessando o painel só para carregar viewport.
  const vp = useViewport();
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        // 40px no toque · os dois botões ("Selecionar todos" e "Limpar") cabem
        // lado a lado com o contador em 304px de painel.
        height: byViewport(vp, { mobile: 40, desktop: 24 }),
        padding: byViewport(vp, { mobile: '0 12px', desktop: '0 9px' }),
        borderRadius: 5,
        background: 'transparent',
        border: `1px solid ${disabled ? p.border : p.borderStrong}`,
        color: disabled ? p.placeholder : accent,
        fontSize: byViewport(vp, { mobile: 11.5, desktop: 10.5 }),
        fontWeight: 700,
        fontFamily: fontSans,
        cursor: disabled ? 'default' : 'pointer',
        // Com altura fixa, deixar o rótulo quebrar vazaria o botão.
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}
